import React, { useState, useEffect } from "react";
import {
  Typography, Button, Container, Box, AppBar, Toolbar, Card, CardContent, Chip,
  Grid, IconButton, Divider, Drawer, List, ListItem, ListItemText, useMediaQuery, 
  useTheme, Fade, Grow, Paper, ClickAwayListener, MenuItem, Stack, Popper, Avatar
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SchoolIcon from "@mui/icons-material/School";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PublicIcon from "@mui/icons-material/Public";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import YouTubeIcon from "@mui/icons-material/YouTube";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import BusinessIcon from "@mui/icons-material/Business";
import GroupsIcon from "@mui/icons-material/Groups";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import VerifiedIcon from "@mui/icons-material/Verified";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import TimelineIcon from "@mui/icons-material/Timeline";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import FeedbackIcon from "@mui/icons-material/Feedback";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PersonIcon from "@mui/icons-material/Person";
import logo from "../assets/logo.png";

// Social media links
const SOCIAL_LINKS = {
  instagram: "https://www.instagram.com/dr_labeebzeeshan/",
  youtube: "https://www.youtube.com/@labeeb1402",
  linkedin: "https://www.linkedin.com/in/dr-labeeb-zeeshan-62245a48"
};

// Corporate Training PDFs
const CORPORATE_TRAINING_PDFS = [
  { 
    label: "Accounting for Non-Accounting Managers",
    file: "/pdfs/accounting_for_non_accounting_managers.pdf"
  },
  { 
    label: "IFRS 9 - Financial Instruments",
    file: "/pdfs/ifrs9.pdf"
  },
  { 
    label: "International Financial Reporting Standards",
    file: "/pdfs/ifrs.pdf"
  },
  { 
    label: "Receivables and Payables",
    file: "/pdfs/receivables_payables.pdf"
  }
];

// Prospectus PDF
const PROSPECTUS_PDF = {
  label: "Dr. Labeeb Academy Prospectus",
  file: "/pdfs/dr_labeeb_academy_prospectus.pdf"
};

// Our Team Members (Navbar Dropdown)
const TEAM_MEMBERS = [
  {
    label: "Dr Labeeb",
    file: "/pdfs/dr_labeeb_cv.pdf",
    icon: <PersonIcon />
  },
  {
    label: "Shazia Sadiq Ali",
    file: "/pdfs/shazia_cv.pdf",
    icon: <PersonIcon />
  }
];

const cyan = "#00cba9";
const pink = "#ec4899";
const orange = "#f97316";
const purple = "#a855f7";

// Team Details for "Our Team" UI Section
const teamDetails = [
  {
    name: "Dr. Labeeb Mohammed Zeeshan",
    role: "Assistant Professor & Management Consultant",
    bio: "With over 28 years of academic and corporate experience, Dr. Labeeb holds a Ph.D. in Finance and is an expert in Enterprise Risk Management and IFRS. He has a proven track record of guiding students to become ACCA prizewinners and transforming professionals into future leaders.",
    color: cyan,
    file: "/pdfs/dr_labeeb_cv.pdf",
    image: "/images/zeeshan.jpg",
    initials: "LMZ"
  },
  {
    name: "Shazia Sadiq Ali",
    role: "ACCA Affiliate & Paper Review Assistant",
    bio: "Shazia is an ACCA Affiliate holding a First Class Honors degree in Accounting and Finance. With practical experience as a Financial Accountant Intern at Deloitte and as an ACCA paper reviewer, she brings strong analytical skills and specialized knowledge in Advanced Audit and Assurance.",
    color: purple,
    file: "/pdfs/shazia_cv.pdf",
    image: "/images/shazia.jpg",
    initials: "SSA"
  }
];

// Feedback Form Link
const FEEDBACK_FORM_URL = "https://forms.cloud.microsoft/Pages/ResponsePage.aspx?id=DQSIkWdsW0yxEjajBLZtrQAAAAAAAAAAAAO__U5xhnBUQlNOTklPM0g0VUdRNEI5UVgzWTZWVDZNWC4u";

// Navigation data — Fee structure now opens the /pdfs/fees.pdf
const quickLinks = [
  { label: "Home", icon: <HomeIcon />, action: () => window.scrollTo({ top: 0, behavior: "smooth" }) },
  { label: "About Us", icon: <InfoIcon />, action: () => window.open("/pdfs/lmc_profile.pdf", "_blank") },
  { label: "Advisory Board", icon: <GroupsIcon />, action: () => window.alert("Advisory Board page coming soon!") },
  { label: "Our Team", icon: <GroupsIcon />, action: () => document.getElementById("team")?.scrollIntoView({ behavior: "smooth" }), hasTeamSubmenu: true },
  { label: "Prospectus", icon: <PictureAsPdfIcon />, action: () => window.open(PROSPECTUS_PDF.file, "_blank") },
  { label: "Fee Structure", icon: <AttachMoneyIcon />, action: () => window.open("/pdfs/fees.pdf", "_blank") },
  { label: "Contact Us", icon: <ContactMailIcon />, action: () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }) }
];

// Services
const services = [
  { label: "Management Consultancy", icon: <BusinessIcon />, action: () => window.alert("Management Consultancy services coming soon!") },
  { label: "Corporate Training", icon: <SchoolIcon />, hasCorporateDropdown: true },
  { label: "Career Counselling", icon: <InfoIcon />, action: () => window.alert("Career Counselling services coming soon!") },
  { label: "Feedback", icon: <FeedbackIcon />, requiresLogin: true }
];

const courses = [
  { 
    id: "ACCA", 
    name: "ACCA", 
    students: "150+", 
    features: ["Live Classes", "Recorded Sessions", "Mock Exams", "Case Studies"], 
    color: cyan,
    learnMoreUrl: "https://www.accaglobal.com/gb/en.html"
  },
  { 
    id: "CMA", 
    name: "CMA", 
    students: "25+", 
    features: ["Interactive Learning", "Live Classes", "Case Studies", "Mock Exams"], 
    color: pink,
    learnMoreUrl: "https://www.imanet.org/"
  },
  { 
    id: "CIA", 
    name: "CIA", 
    students: "25+", 
    features: ["Live Classes", "Recorded Sessions", "Mock Exams", "Case Studies"], 
    color: orange,
    learnMoreUrl: "https://www.theiia.org/en/certifications/cia/"
  },
  { 
    id: "CFA", 
    name: "CFA", 
    students: "25+", 
    features: ["Comprehensive Curriculum", "Industry Experts", "Research Tools", "Networking"], 
    color: purple,
    learnMoreUrl: "https://www.cfainstitute.org/"
  }
];

// Success data
const successData = [
  { name: "Maryam Al Balushi", level: "Strategic Professional", paper: "Strategic Business Leadership (SBL)", marks: 72, achievement: "Prizewinner", nationality: "Omani", period: "June 2025", color: cyan },
  { name: "Svetlana Dsouza", level: "Strategic Professional", paper: "Advanced Financial Management (AFM)", marks: 64, achievement: "Pass", nationality: "Indian", period: "June 2025", color: cyan },
  { name: "Mohammed Ismail", level: "Applied Knowledge", paper: "Taxation (UK)", marks: 68, achievement: "Pass", nationality: "Indian", period: "March 2025", color: pink },
  { name: "Frederick", level: "Applied Knowledge", paper: "Taxation (UK)", marks: 60, achievement: "Pass", nationality: "Nigeria", period: "March 2025", color: pink },
  { name: "Maryam Al Balushi", level: "Strategic Professional", paper: "Strategic Business Reporting", marks: 85, achievement: "Prizewinner", nationality: "Omani", period: "December 2024", color: orange },
  { name: "Svetlana Dsouza", level: "Strategic Professional", paper: "Strategic Business Reporting", marks: 68, achievement: "Pass", nationality: "Indian", period: "December 2024", color: orange },
  { name: "Mohammed Ismail", level: "Applied Knowledge", paper: "Financial Accounting FA", marks: 94, achievement: "Certificate of Achievement", nationality: "Indian", period: "September 2024", color: purple },
  { name: "Mohammed Ismail", level: "Applied Knowledge", paper: "Management Accounting MA", marks: 86, achievement: "Certificate of Achievement", nationality: "Indian", period: "September 2024", color: purple }
];

// Animated rotating globe component
function AnimatedGlobe() {
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setRotation(prev => (prev + 0.5) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        height: "500px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative"
      }}
    >
      <svg width={400} height={400} viewBox="0 0 400 400">
        <defs>
          <radialGradient id="globeGradient">
            <stop offset="0%" stopColor={cyan} stopOpacity={0.15} />
            <stop offset="100%" stopColor={cyan} stopOpacity={0} />
          </radialGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
 
        {/* Background glow */}
        <circle cx={200} cy={200} r={160} fill="url(#globeGradient)" />
 
        {/* Main globe circle */}
        <circle
          cx={200}
          cy={200}
          r={150}
          fill="none"
          stroke={cyan}
          strokeWidth={2.5}
          opacity={0.7}
          filter="url(#glow)"
        />
 
        {/* Rotating longitude lines */}
        <g
          style={{
            transform: `rotate(${rotation}deg)`,
            transformOrigin: "200px 200px",
            transition: "transform 0.05s linear"
          }}
        >
          {[0, 30, 60, 90, 120, 150].map((angle) => (
            <ellipse
              key={`lon-${angle}`}
              cx={200}
              cy={200}
              rx={150}
              ry={150}
              fill="none"
              stroke={cyan}
              strokeWidth={1}
              opacity={0.35}
            />
          ))}
        </g>
 
        {/* Static latitude lines */}
        {[-90, -60, -30, 0, 30, 60, 90].map((lat) => {
          const y = 200 - (lat * 150) / 90;
          const rx = Math.abs(Math.cos((lat * Math.PI) / 180)) * 150;
          return (
            <ellipse
              key={`lat-${lat}`}
              cx={200}
              cy={y}
              rx={rx}
              ry={8}
              fill="none"
              stroke={cyan}
              strokeWidth={1}
              opacity={0.35}
            />
          );
        })}
 
        {/* Animated country dots */}
        <g
          style={{
            transform: `rotate(${rotation}deg)`,
            transformOrigin: "200px 200px",
            transition: "transform 0.05s linear"
          }}
        >
          {[
            { x: 250, y: 150 }, { x: 280, y: 180 }, { x: 260, y: 200 },
            { x: 150, y: 140 }, { x: 170, y: 170 }, { x: 140, y: 190 },
            { x: 220, y: 120 }, { x: 240, y: 280 }, { x: 180, y: 250 },
            { x: 290, y: 220 }, { x: 120, y: 210 }, { x: 200, y: 100 },
            { x: 200, y: 300 }, { x: 160, y: 230 }, { x: 270, y: 160 }
          ].map((dot, idx) => (
            <circle
              key={`dot-${idx}`}
              cx={dot.x}
              cy={dot.y}
              r={3}
              fill={cyan}
              opacity={0.8}
              filter="url(#glow)"
            >
              <animate
                attributeName="r"
                values="2;4;2"
                dur="2s"
                begin={`${idx * 0.2}s`}
                repeatCount="indefinite"
              />
            </circle>
          ))}
        </g>
      </svg>
 
      <Box sx={{ textAlign: "center", mt: 3, animation: "fadeInUp 1s ease-out" }}>
        <Typography variant="h6" fontWeight={700} color={cyan} mb={0.5}>
          Global Reach
        </Typography>
        <Typography variant="body2" color="#64748b">
          Students from 12+ Countries
        </Typography>
      </Box>
    </Box>
  );
}

function Home() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [visible, setVisible] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  
  // Team submenu anchor (used with Popper for "Our Team")
  const [teamAnchor, setTeamAnchor] = useState(null);
  const [teamSubmenuOpen, setTeamSubmenuOpen] = useState(false);
  
  // Corporate training submenu
  const [corporateTrainingAnchor, setCorporateTrainingAnchor] = useState(null);

  useEffect(() => {
    setVisible(true);
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleDropdownToggle = (key) => {
    setOpenDropdown(openDropdown === key ? null : key);
  };

  const handleDropdownClose = () => {
    setOpenDropdown(null);
    setTeamAnchor(null);
    setTeamSubmenuOpen(false);
    setCorporateTrainingAnchor(null);
  };

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  return (
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", overflow: "hidden" }}>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
          background: `radial-gradient(circle at ${50 + scrollY * 0.02}% ${50 + scrollY * 0.01}%, ${cyan}08 0%, transparent 50%)`,
          opacity: 0.5
        }}
      />

      <AppBar
        position="sticky"
        sx={{
          bgcolor: scrollY > 50 ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.98)",
          backdropFilter: "blur(20px)",
          boxShadow: scrollY > 50 ? "0 4px 20px rgba(0,0,0,0.12)" : "0 2px 12px rgba(0,0,0,0.08)",
          transition: "all 0.3s ease",
          zIndex: 1100
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ alignItems: "center", py: 1.5, px: 0 }}>
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              sx={{ minWidth: 250, cursor: "pointer" }}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              <img
                src={logo}
                alt="Dr. Labeeb Academy Logo"
                style={{
                  height: isMobile ? 43 : 55,
                  transition: "transform 0.3s ease"
                }}
                onMouseEnter={(e) => (e.target.style.transform = "scale(1.08) rotate(3deg)")}
                onMouseLeave={(e) => (e.target.style.transform = "scale(1) rotate(0deg)")}
              />
              {!isMobile && (
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{
                      background: `linear-gradient(135deg, ${cyan}, ${purple})`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      letterSpacing: "-0.5px"
                    }}
                  >
                    Dr. Labeeb Academy
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    color="#94a3b8"
                    fontWeight={600}
                    sx={{ letterSpacing: "0.2px" }}
                  >
                    Labeeb Management Consultants
                  </Typography>
                </Box>
              )}
            </Box>

            {!isMobile && (
              <Box display="flex" alignItems="center" gap={0.5} sx={{ ml: "auto" }}>

                {/* Quick Links dropdown */}
                <ClickAwayListener onClickAway={() => openDropdown === "quick" && handleDropdownClose()}>
                  <Box sx={{ position: "relative" }}>
                    <Button
                      endIcon={
                        <ExpandMoreIcon
                          sx={{
                            transition: "transform 0.3s ease",
                            transform: openDropdown === "quick" ? "rotate(180deg)" : "rotate(0deg)"
                          }}
                        />
                      }
                      onClick={() => handleDropdownToggle("quick")}
                      sx={{
                        fontWeight: 700,
                        color: openDropdown === "quick" ? cyan : "#475569",
                        px: 2.5,
                        py: 1,
                        borderRadius: 2,
                        transition: "all 0.2s ease",
                        "&:hover": { bgcolor: `${cyan}10`, color: cyan }
                      }}
                    >
                      Quick Links
                    </Button>

                    <Fade in={openDropdown === "quick"}>
                      <Paper
                        sx={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          mt: 1,
                          minWidth: 220,
                          borderRadius: 3,
                          boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
                          overflow: "visible",
                          zIndex: 1300,
                          display: openDropdown === "quick" ? "block" : "none"
                        }}
                      >
                        {quickLinks.map((link) => (
                          <Box key={link.label} sx={{ position: "relative" }}>
                            <MenuItem
                              onClick={() => {
                                if (!link.hasTeamSubmenu) {
                                  link.action();
                                  handleDropdownClose();
                                }
                              }}
                              onMouseEnter={(e) => {
                                if (link.hasTeamSubmenu) {
                                  setTeamAnchor(e.currentTarget);
                                  setTeamSubmenuOpen(true);
                                } else {
                                  setTeamSubmenuOpen(false);
                                  setTeamAnchor(null);
                                }
                              }}
                              sx={{
                                px: 3,
                                py: 1.5,
                                gap: 2,
                                transition: "all 0.2s ease",
                                borderLeft: "3px solid transparent",
                                "&:hover": {
                                  bgcolor: `${cyan}08`,
                                  borderLeftColor: cyan,
                                  transform: "translateX(4px)"
                                }
                              }}
                            >
                              <Box sx={{ color: cyan, display: "flex", alignItems: "center" }}>
                                {link.icon}
                              </Box>
                              <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                                {link.label}
                              </Typography>
                              {link.hasTeamSubmenu && (
                                <ExpandMoreIcon
                                  sx={{ fontSize: 16, color: "#94a3b8", transform: "rotate(-90deg)" }}
                                />
                              )}
                            </MenuItem>

                            {/* Our Team submenu — rendered inline so it never disappears */}
                            {link.hasTeamSubmenu && teamSubmenuOpen && teamAnchor && (
                              <Paper
                                onMouseEnter={() => setTeamSubmenuOpen(true)}
                                onMouseLeave={() => {
                                  setTeamSubmenuOpen(false);
                                  setTeamAnchor(null);
                                }}
                                sx={{
                                  position: "absolute",
                                  left: "100%",
                                  top: 0,
                                  ml: 0.5,
                                  minWidth: 220,
                                  borderRadius: 3,
                                  boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
                                  overflow: "hidden",
                                  zIndex: 1400
                                }}
                              >
                                {TEAM_MEMBERS.map((member) => (
                                  <MenuItem
                                    key={member.label}
                                    onClick={() => {
                                      window.open(member.file, "_blank");
                                      setTeamSubmenuOpen(false);
                                      handleDropdownClose();
                                    }}
                                    sx={{
                                      px: 3,
                                      py: 1.5,
                                      gap: 2,
                                      transition: "all 0.2s ease",
                                      borderLeft: "3px solid transparent",
                                      "&:hover": {
                                        bgcolor: `${purple}08`,
                                        borderLeftColor: purple,
                                        transform: "translateX(4px)"
                                      }
                                    }}
                                  >
                                    <Box sx={{ color: purple, display: "flex", alignItems: "center" }}>
                                      <PictureAsPdfIcon sx={{ fontSize: 20 }} />
                                    </Box>
                                    <Typography variant="body2" fontWeight={600}>
                                      {member.label}
                                    </Typography>
                                  </MenuItem>
                                ))}
                              </Paper>
                            )}
                          </Box>
                        ))}
                      </Paper>
                    </Fade>
                  </Box>
                </ClickAwayListener>

                {/* Strategic Partners dropdown */}
                <ClickAwayListener onClickAway={() => openDropdown === "cert" && handleDropdownClose()}>
                  <Box sx={{ position: "relative" }}>
                    <Button
                      endIcon={
                        <ExpandMoreIcon
                          sx={{
                            transition: "transform 0.3s ease",
                            transform: openDropdown === "cert" ? "rotate(180deg)" : "rotate(0deg)"
                          }}
                        />
                      }
                      onClick={() => handleDropdownToggle("cert")}
                      sx={{
                        fontWeight: 700,
                        color: openDropdown === "cert" ? cyan : "#475569",
                        px: 2.5,
                        py: 1,
                        borderRadius: 2,
                        transition: "all 0.2s ease",
                        "&:hover": { bgcolor: `${cyan}10`, color: cyan }
                      }}
                    >
                      Strategic Partners
                    </Button>
                    <Fade in={openDropdown === "cert"}>
                      <Paper
                        sx={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          mt: 1,
                          minWidth: 240,
                          borderRadius: 3,
                          boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
                          overflow: "hidden",
                          zIndex: 1300,
                          display: openDropdown === "cert" ? "block" : "none"
                        }}
                      >
                        <MenuItem
                          onClick={() => {
                            window.open("https://kaplanpublishing.co.uk/", "_blank");
                            handleDropdownClose();
                          }}
                          sx={{
                            px: 3,
                            py: 1.5,
                            transition: "all 0.2s ease",
                            borderLeft: "3px solid transparent",
                            "&:hover": {
                              bgcolor: `${pink}08`,
                              borderLeftColor: pink,
                              transform: "translateX(4px)"
                            }
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            Kaplan Publishing UK
                          </Typography>
                        </MenuItem>
                      </Paper>
                    </Fade>
                  </Box>
                </ClickAwayListener>

                {/* Services dropdown */}
                <ClickAwayListener onClickAway={() => openDropdown === "serv" && handleDropdownClose()}>
                  <Box sx={{ position: "relative" }}>
                    <Button
                      endIcon={
                        <ExpandMoreIcon
                          sx={{
                            transition: "transform 0.3s ease",
                            transform: openDropdown === "serv" ? "rotate(180deg)" : "rotate(0deg)"
                          }}
                        />
                      }
                      onClick={() => handleDropdownToggle("serv")}
                      sx={{
                        fontWeight: 700,
                        color: openDropdown === "serv" ? cyan : "#475569",
                        px: 2.5,
                        py: 1,
                        borderRadius: 2,
                        transition: "all 0.2s ease",
                        "&:hover": { bgcolor: `${cyan}10`, color: cyan }
                      }}
                    >
                      Services
                    </Button>
                    <Fade in={openDropdown === "serv"}>
                      <Paper
                        sx={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          mt: 1,
                          minWidth: 240,
                          borderRadius: 3,
                          boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
                          overflow: "visible",
                          zIndex: 1300,
                          display: openDropdown === "serv" ? "block" : "none"
                        }}
                      >
                        {services.map((serv) => (
                          <Box key={serv.label} sx={{ position: "relative" }}>
                            <MenuItem
                              onClick={() => {
                                if (serv.requiresLogin) {
                                  navigate("/login", { state: { redirectTo: FEEDBACK_FORM_URL } });
                                  handleDropdownClose();
                                } else if (!serv.hasCorporateDropdown) {
                                  serv.action();
                                  handleDropdownClose();
                                }
                              }}
                              onMouseEnter={(e) => {
                                if (serv.hasCorporateDropdown) {
                                  setCorporateTrainingAnchor(e.currentTarget);
                                } else {
                                  setCorporateTrainingAnchor(null);
                                }
                              }}
                              sx={{
                                px: 3,
                                py: 1.5,
                                gap: 2,
                                transition: "all 0.2s ease",
                                borderLeft: "3px solid transparent",
                                "&:hover": {
                                  bgcolor: `${orange}08`,
                                  borderLeftColor: orange,
                                  transform: "translateX(4px)"
                                }
                              }}
                            >
                              <Box sx={{ color: orange, display: "flex", alignItems: "center" }}>
                                {serv.icon}
                              </Box>
                              <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                                {serv.label}
                              </Typography>
                              {serv.hasCorporateDropdown && (
                                <ExpandMoreIcon
                                  sx={{ fontSize: 16, color: "#94a3b8", transform: "rotate(-90deg)" }}
                                />
                              )}
                            </MenuItem>

                            {/* Corporate Training PDFs inline submenu */}
                            {serv.hasCorporateDropdown && corporateTrainingAnchor && (
                              <Paper
                                onMouseEnter={() => setCorporateTrainingAnchor(corporateTrainingAnchor)}
                                onMouseLeave={() => setCorporateTrainingAnchor(null)}
                                sx={{
                                  position: "absolute",
                                  left: "100%",
                                  top: 0,
                                  ml: 0.5,
                                  minWidth: 320,
                                  borderRadius: 3,
                                  boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
                                  overflow: "hidden",
                                  zIndex: 1400
                                }}
                              >
                                <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e2e8f0" }}>
                                  <Typography variant="subtitle2" fontWeight={700} color={orange}>
                                    Corporate Training Programs
                                  </Typography>
                                </Box>
                                {CORPORATE_TRAINING_PDFS.map((pdf, index) => (
                                  <MenuItem
                                    key={index}
                                    onClick={() => {
                                      window.open(pdf.file, "_blank");
                                      setCorporateTrainingAnchor(null);
                                      handleDropdownClose();
                                    }}
                                    sx={{
                                      px: 3,
                                      py: 1.5,
                                      gap: 2,
                                      transition: "all 0.2s ease",
                                      borderLeft: "3px solid transparent",
                                      "&:hover": {
                                        bgcolor: `${orange}08`,
                                        borderLeftColor: orange,
                                        transform: "translateX(4px)"
                                      }
                                    }}
                                  >
                                    <Box sx={{ color: orange, display: "flex", alignItems: "center" }}>
                                      <PictureAsPdfIcon />
                                    </Box>
                                    <Typography variant="body2" fontWeight={600} fontSize={14}>
                                      {pdf.label}
                                    </Typography>
                                  </MenuItem>
                                ))}
                              </Paper>
                            )}
                          </Box>
                        ))}
                      </Paper>
                    </Fade>
                  </Box>
                </ClickAwayListener>

                {/* Online Learning Support dropdown */}
                <ClickAwayListener onClickAway={() => openDropdown === "courses" && handleDropdownClose()}>
                  <Box sx={{ position: "relative" }}>
                    <Button
                      endIcon={
                        <ExpandMoreIcon
                          sx={{
                            transition: "transform 0.3s ease",
                            transform: openDropdown === "courses" ? "rotate(180deg)" : "rotate(0deg)"
                          }}
                        />
                      }
                      onClick={() => handleDropdownToggle("courses")}
                      sx={{
                        fontWeight: 700,
                        color: openDropdown === "courses" ? cyan : "#475569",
                        px: 2.5,
                        py: 1,
                        borderRadius: 2,
                        transition: "all 0.2s ease",
                        "&:hover": { bgcolor: `${cyan}10`, color: cyan }
                      }}
                    >
                      Online Learning Support
                    </Button>
                    <Fade in={openDropdown === "courses"}>
                      <Paper
                        sx={{
                          position: "absolute",
                          top: "100%",
                          right: 0,
                          mt: 1,
                          minWidth: 320,
                          borderRadius: 3,
                          boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
                          overflow: "hidden",
                          zIndex: 1300,
                          p: 2,
                          display: openDropdown === "courses" ? "block" : "none"
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          color="#64748b"
                          mb={1.5}
                          px={1}
                        >
                          Available Programs
                        </Typography>
                        {courses.map((course) => (
                          <Box
                            key={course.id}
                            onClick={() => {
                              document.getElementById(course.id)?.scrollIntoView({ behavior: "smooth" });
                              handleDropdownClose();
                            }}
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              cursor: "pointer",
                              mb: 1,
                              border: `1px solid ${course.color}20`,
                              transition: "all 0.3s ease",
                              "&:hover": {
                                bgcolor: `${course.color}08`,
                                borderColor: course.color,
                                transform: "translateX(4px)"
                              }
                            }}
                          >
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                              <Typography variant="body1" fontWeight={700} color={course.color}>
                                {course.name}
                              </Typography>
                              <Chip
                                label={course.students}
                                size="small"
                                sx={{
                                  bgcolor: `${course.color}20`,
                                  color: course.color,
                                  fontWeight: 700,
                                  fontSize: "11px"
                                }}
                              />
                            </Box>
                            <Typography variant="caption" color="#64748b">
                              {course.features.slice(0, 2).join(" • ")}
                            </Typography>
                          </Box>
                        ))}
                      </Paper>
                    </Fade>
                  </Box>
                </ClickAwayListener>

                <Button
                  variant="outlined"
                  startIcon={<PersonAddIcon />}
                  onClick={() => navigate("/signup")}
                  sx={{
                    borderColor: cyan,
                    color: cyan,
                    fontWeight: 700,
                    px: 3,
                    py: 1,
                    ml: 2,
                    borderRadius: 2.5,
                    textTransform: "none",
                    fontSize: 15,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      borderColor: cyan,
                      bgcolor: `${cyan}10`,
                      transform: "translateY(-2px)"
                    }
                  }}
                >
                  Create Account
                </Button>

                <Button
                  variant="contained"
                  onClick={() => navigate("/login")}
                  sx={{
                    background: `linear-gradient(135deg, ${cyan}, ${purple})`,
                    fontWeight: 700,
                    px: 3,
                    py: 1,
                    ml: 2,
                    borderRadius: 2.5,
                    textTransform: "none",
                    boxShadow: "0 4px 14px rgba(0,203,169,0.3)",
                    fontSize: 15,
                    position: "relative",
                    overflow: "hidden",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: "-100%",
                      width: "100%",
                      height: "100%",
                      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                      transition: "left 0.5s"
                    },
                    "&:hover": {
                      transform: "translateY(-2px) scale(1.03)",
                      boxShadow: "0 8px 24px rgba(0,203,169,0.4)",
                      "&:before": { left: "100%" }
                    }
                  }}
                >
                  Login
                </Button>
              </Box>
            )}

            {isMobile && (
              <IconButton
                onClick={toggleMobileMenu}
                sx={{
                  color: "#64748b",
                  ml: "auto",
                  transition: "all 0.3s ease",
                  "&:hover": { transform: "rotate(90deg)", color: cyan }
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
        sx={{ ".MuiDrawer-paper": { width: 280, bgcolor: "white" } }}
      >
        <Box sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" fontWeight={700} color={cyan}>
              Menu
            </Typography>
            <IconButton onClick={toggleMobileMenu}>
              <CloseIcon />
            </IconButton>
          </Box>
          <List>
            {quickLinks.map((item) => (
              <Box key={item.label}>
                <ListItem
                  button
                  onClick={() => {
                    if (!item.hasTeamSubmenu) {
                      item.action();
                      toggleMobileMenu();
                    }
                  }}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    transition: "all 0.2s ease",
                    "&:hover": { bgcolor: `${cyan}10`, transform: "translateX(5px)" }
                  }}
                >
                  <Box sx={{ mr: 2, color: cyan, display: "flex" }}>{item.icon}</Box>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontWeight: 600, color: "#64748b" }}
                  />
                </ListItem>
              </Box>
            ))}

            {/* Mobile: Team members */}
            <Box sx={{ pl: 2, mb: 1 }}>
              <Typography variant="caption" fontWeight={700} color="#94a3b8" sx={{ pl: 1 }}>
                Our Team
              </Typography>
              {TEAM_MEMBERS.map((member) => (
                <ListItem
                  button
                  key={member.label}
                  onClick={() => {
                    window.open(member.file, "_blank");
                    toggleMobileMenu();
                  }}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    "&:hover": { bgcolor: `${purple}10` }
                  }}
                >
                  <Box sx={{ mr: 2, color: purple, display: "flex" }}>
                    <PictureAsPdfIcon fontSize="small" />
                  </Box>
                  <ListItemText
                    primary={member.label}
                    primaryTypographyProps={{ fontWeight: 600, color: "#64748b", fontSize: 14 }}
                  />
                </ListItem>
              ))}
            </Box>

            <ListItem sx={{ mt: 2 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  navigate("/login");
                  toggleMobileMenu();
                }}
                sx={{
                  background: `linear-gradient(135deg, ${cyan}, ${purple})`,
                  fontWeight: 700,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 4px 12px ${cyan}60`
                  }
                }}
              >
                Login
              </Button>
            </ListItem>
          </List>
        </Box>
      </Drawer>

      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Fade in={visible} timeout={1000}>
          <Container maxWidth="lg" sx={{ pt: isMobile ? 6 : 12, pb: 8 }}>
            <Box
              display="flex"
              flexDirection={isMobile ? "column" : "row"}
              alignItems="center"
              justifyContent="space-between"
              gap={isMobile ? 6 : 8}
            >
              <Box flex={1} sx={{ animation: "slideInLeft 0.8s ease-out" }}>
                <Typography
                  variant={isMobile ? "h4" : "h3"}
                  fontWeight={900}
                  color="#1e293b"
                  lineHeight={1.2}
                  mb={3}
                >
                  Expert-led{" "}
                  <span
                    style={{
                      background: `linear-gradient(135deg, ${cyan}, ${pink})`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text"
                    }}
                  >
                    Interactive
                  </span>{" "}
                  e-Learning
                </Typography>

                <Typography
                  variant={isMobile ? "body1" : "h6"}
                  color="#64748b"
                  mb={4}
                  lineHeight={1.6}
                >
                  Master financial certifications with TIME, VALUE, and MONEY optimization. Join
                  1000+ professionals advancing their careers through ACCA, CMA, CIA, CFA.
                </Typography>
                <Box display="flex" gap={2} flexDirection={isMobile ? "column" : "row"}>
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<PlayArrowIcon />}
                    onClick={() => document.getElementById("courses")?.scrollIntoView({ behavior: "smooth" })}
                    sx={{
                      background: `linear-gradient(135deg, ${cyan}, ${purple})`,
                      fontWeight: 700,
                      px: 4,
                      py: 1.5,
                      borderRadius: 3,
                      fontSize: 16,
                      textTransform: "none",
                      boxShadow: "0 4px 14px rgba(0,203,169,0.4)",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-3px) scale(1.05)",
                        boxShadow: "0 8px 25px rgba(0,203,169,0.5)"
                      }
                    }}
                  >
                    Explore Courses
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => window.open(SOCIAL_LINKS.youtube, "_blank")}
                    sx={{
                      borderColor: cyan,
                      color: cyan,
                      fontWeight: 700,
                      px: 4,
                      py: 1.5,
                      borderRadius: 3,
                      fontSize: 16,
                      textTransform: "none",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        borderColor: cyan,
                        bgcolor: `${cyan}10`,
                        transform: "translateY(-3px) scale(1.05)"
                      }
                    }}
                  >
                    Watch Demo
                  </Button>
                </Box>
              </Box>

              {!isMobile && (
                <Box
                  flex={1}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    transform: `translateY(${scrollY * 0.1}px)`,
                    transition: "transform 0.1s ease-out"
                  }}
                >
                  <AnimatedGlobe />
                </Box>
              )}
            </Box>
          </Container>
        </Fade>
      </Box>

      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Grid container spacing={4}>
            {[
              {
                icon: <TrendingUpIcon fontSize="large" sx={{ color: cyan }} />,
                title: "TIME",
                desc: "Optimized learning paths tailored to your goals.",
                delay: 0,
                bgColor: `${cyan}08`
              },
              {
                icon: <SchoolIcon fontSize="large" sx={{ color: pink }} />,
                title: "VALUE",
                desc: "Maximum career return with expert support.",
                delay: 0.2,
                bgColor: `${pink}08`
              },
              {
                icon: <PublicIcon fontSize="large" sx={{ color: orange }} />,
                title: "MONEY",
                desc: "Affordable programs, global career growth.",
                delay: 0.4,
                bgColor: `${orange}08`
              }
            ].map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Grow
                  in={visible}
                  timeout={1000}
                  style={{ transformOrigin: "0 0 0" }}
                  {...(visible ? { timeout: 1000 + feature.delay * 1000 } : {})}
                >
                  <Card
                    sx={{
                      textAlign: "center",
                      p: 4,
                      bgcolor: "white",
                      borderRadius: 4,
                      border: `2px solid transparent`,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      position: "relative",
                      overflow: "hidden",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background: `linear-gradient(90deg, ${cyan}, ${pink}, ${orange})`,
                        transform: "translateX(-100%)",
                        transition: "transform 0.5s ease"
                      },
                      "&:hover": {
                        transform: "translateY(-12px) scale(1.03)",
                        boxShadow: "0 16px 40px rgba(0,0,0,0.12)",
                        bgcolor: feature.bgColor,
                        "&:before": { transform: "translateX(0)" }
                      }
                    }}
                  >
                    <Box
                      sx={{
                        mb: 2,
                        transform: "scale(1)",
                        transition: "transform 0.3s ease",
                        "&:hover": { transform: "scale(1.1) rotate(5deg)" }
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" fontWeight={700} color="#1e293b" mb={1}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="#64748b">
                      {feature.desc}
                    </Typography>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Courses Section */}
      <Box id="courses" sx={{ bgcolor: "white", py: 10, position: "relative", zIndex: 1 }}>
        <Container maxWidth="lg">
          <Fade in={visible} timeout={1200}>
            <Box>
              <Typography
                variant="h5"
                fontWeight={700}
                color={cyan}
                textAlign="center"
                mb={3}
                sx={{ letterSpacing: "0.5px", opacity: 0.9 }}
              >
                Virtual Live Online Teaching Learning Support Platform
              </Typography>
              <Typography variant="body1" color="#94a3b8" mt={1} textAlign="center" mb={6}>
                Master globally recognized certifications with our expert-led interactive platform.
              </Typography>
            </Box>
          </Fade>
          <Grid container spacing={4}>
            {courses.map((course, idx) => (
              <Grid item xs={12} md={6} key={course.id}>
                <Grow
                  in={visible}
                  timeout={1000}
                  style={{ transformOrigin: "0 0 0" }}
                  {...(visible ? { timeout: 1200 + idx * 200 } : {})}
                >
                  <Card
                    id={course.id}
                    sx={{
                      borderRadius: 4,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                      border: `2px solid ${course.color}20`,
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      overflow: "hidden",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "6px",
                        background: course.color,
                        transform: "scaleX(0)",
                        transformOrigin: "left",
                        transition: "transform 0.3s ease"
                      },
                      "&:hover": {
                        boxShadow: "0 16px 48px rgba(0,0,0,0.16)",
                        transform: "translateY(-10px) scale(1.02)",
                        borderColor: course.color,
                        "&:before": { transform: "scaleX(1)" }
                      }
                    }}
                  >
                    <CardContent sx={{ p: 4, flexGrow: 1, display: "flex", flexDirection: "column" }}>
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                        <Typography variant="h4" fontWeight={900} color={course.color} mb={0.5}>
                          {course.name}
                        </Typography>
                        <Chip
                          icon={<WorkspacePremiumIcon sx={{ fontSize: 16 }} />}
                          label={`${course.students} Students`}
                          size="small"
                          sx={{
                            bgcolor: `${course.color}20`,
                            color: course.color,
                            fontWeight: 700,
                            transition: "all 0.3s ease",
                            "&:hover": { bgcolor: course.color, color: "white" }
                          }}
                        />
                      </Box>
                      <Typography variant="body2" color="#64748b" mb={3} lineHeight={1.6} flexGrow={1}>
                        {course.features.join(" • ")}
                      </Typography>
                      <Box display="flex" gap={2}>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => navigate("/signup")}
                          sx={{
                            bgcolor: course.color,
                            fontWeight: 700,
                            py: 1.2,
                            borderRadius: 2,
                            textTransform: "none",
                            boxShadow: `0 4px 14px ${course.color}40`,
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                              bgcolor: course.color,
                              transform: "translateY(-3px) scale(1.05)",
                              boxShadow: `0 8px 24px ${course.color}60`
                            }
                          }}
                        >
                          Enroll Now
                        </Button>
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => window.open(course.learnMoreUrl, "_blank")}
                          sx={{
                            borderColor: course.color,
                            color: course.color,
                            fontWeight: 700,
                            py: 1.2,
                            borderRadius: 2,
                            textTransform: "none",
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                              borderColor: course.color,
                              bgcolor: `${course.color}15`,
                              transform: "translateY(-3px) scale(1.05)"
                            }
                          }}
                        >
                          Learn More
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Success Stories */}
      <Box sx={{ position: "relative", zIndex: 1, py: 10 }}>
        <Container maxWidth="lg">
          <Fade in={visible} timeout={1000}>
            <Box mb={6}>
              <Box display="flex" alignItems="center" justifyContent="center" gap={2} mb={2}>
                <EmojiEventsIcon sx={{ fontSize: 40, color: cyan }} />
                <Typography variant="h3" fontWeight={900} color="#1e293b" textAlign="center">
                  Success{" "}
                  <span
                    style={{
                      background: `linear-gradient(135deg, ${cyan}, ${pink})`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent"
                    }}
                  >
                    Stories
                  </span>
                </Typography>
              </Box>
              <Typography variant="body1" color="#64748b" textAlign="center" maxWidth="700px" mx="auto">
                Celebrating the achievements of our exceptional students. From prizewinners
                to high achievers, our students consistently excel in their professional certifications.
              </Typography>
            </Box>
          </Fade>

          <Grid container spacing={3}>
            {successData.map((student, idx) => (
              <Grid item xs={12} md={6} key={idx}>
                <Grow
                  in={visible}
                  timeout={800}
                  style={{ transformOrigin: "0 0 0" }}
                  {...(visible ? { timeout: 800 + idx * 100 } : {})}
                >
                  <Card
                    sx={{
                      borderRadius: 3,
                      border: `2px solid ${student.color}20`,
                      position: "relative",
                      overflow: "hidden",
                      transition: "all 0.3s ease",
                      "&:before": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: "5px",
                        background: student.color,
                        transform: "scaleY(0)",
                        transformOrigin: "top",
                        transition: "transform 0.3s ease"
                      },
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: `0 12px 30px ${student.color}30`,
                        borderColor: student.color,
                        "&:before": { transform: "scaleY(1)" }
                      }
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                        <Box flex={1}>
                          <Typography variant="h6" fontWeight={700} color={student.color} mb={0.5}>
                            {student.name}
                          </Typography>
                          <Typography variant="body2" color="#64748b" mb={1}>
                            {student.level}
                          </Typography>
                        </Box>
                        {student.achievement === "Prizewinner" && (
                          <Chip
                            icon={<EmojiEventsIcon sx={{ fontSize: 16 }} />}
                            label="Prizewinner"
                            size="small"
                            sx={{
                              bgcolor: `${student.color}20`,
                              color: student.color,
                              fontWeight: 700,
                              border: `1px solid ${student.color}`
                            }}
                          />
                        )}
                        {student.achievement === "Certificate of Achievement" && (
                          <Chip
                            icon={<VerifiedIcon sx={{ fontSize: 16 }} />}
                            label="Certificate"
                            size="small"
                            sx={{ bgcolor: `${student.color}20`, color: student.color, fontWeight: 700 }}
                          />
                        )}
                      </Box>

                      <Box bgcolor={`${student.color}08`} borderRadius={2} p={2} mb={2}>
                        <Typography variant="body2" fontWeight={600} color="#1e293b" mb={0.5}>
                          {student.paper}
                        </Typography>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          <Chip label={`Marks: ${student.marks}`} size="small" sx={{ bgcolor: "white", fontWeight: 600, fontSize: "12px" }} />
                          <Chip label={student.nationality} size="small" sx={{ bgcolor: "white", fontWeight: 600, fontSize: "12px" }} />
                          <Chip label={student.period} size="small" sx={{ bgcolor: "white", fontWeight: 600, fontSize: "12px" }} />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* About Section */}
      <Box id="about" sx={{ bgcolor: "white", py: 10, position: "relative", zIndex: 1 }}>
        <Container maxWidth="lg">
          <Fade in={visible} timeout={1000}>
            <Box mb={6}>
              <Typography variant="h3" fontWeight={900} color="#1e293b" mb={2} textAlign="center">
                About{" "}
                <span
                  style={{
                    background: `linear-gradient(135deg, ${cyan}, ${purple})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent"
                  }}
                >
                  Dr. Labeeb Mohammed Zeeshan
                </span>
              </Typography>
              <Typography variant="h6" fontWeight={600} color={cyan} textAlign="center" mb={4}>
                Assistant Professor (Finance & Accounting) | Management & Training Consultant
              </Typography>
            </Box>
          </Fade>

          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Card
                sx={{
                  p: 4,
                  borderRadius: 4,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  border: `2px solid ${cyan}10`,
                  transition: "all 0.3s ease",
                  "&:hover": { boxShadow: "0 8px 32px rgba(0,0,0,0.12)", borderColor: cyan }
                }}
              >
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <AutoStoriesIcon sx={{ fontSize: 32, color: cyan }} />
                  <Typography variant="h5" fontWeight={700} color={cyan}>
                    Professional Journey
                  </Typography>
                </Box>

                <Typography variant="body1" color="#64748b" paragraph lineHeight={1.8}>
                  Dr. Labeeb Mohammed Zeeshan holds a Ph.D. in Finance, Masters in Business
                  Management, and Master's in Commerce, and is currently pursuing ACCA. With
                  over 27 years of experience across academic and corporate training, he
                  brings a wealth of knowledge to every session.
                </Typography>

                <Typography variant="body1" color="#64748b" paragraph lineHeight={1.8}>
                  Since 2006, Dr. Labeeb has been associated with leading accredited
                  professional institutions globally and served at the College of Banking
                  and Financial Studies Oman for 16 years (2006 to 2022). His expertise spans
                  certified courses training including ACCA, CIA, CRM, CISI, and CMA exams
                  based preparatory and training sessions.
                </Typography>

                <Typography variant="body1" color="#64748b" paragraph lineHeight={1.8}>
                  He specializes in training working professionals in the banking industry
                  and young graduates with current requirements, giving them exposure to the
                  current changing condition with emphasis on NEXT G, Information
                  Technology, and Artificial Intelligence expectations.
                </Typography>

                <Typography variant="body1" color="#64748b" paragraph lineHeight={1.8}>
                  Dr. Labeeb has rich and real-time experience in Enterprise Risk Management
                  (ERM), Investment Management, Portfolio Management, Wealth Management,
                  Professional Ethical aspects, Mechanics of Stock Market, and Trade Finance.
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <TimelineIcon sx={{ fontSize: 28, color: pink }} />
                  <Typography variant="h6" fontWeight={700} color={pink}>
                    Areas of Expertise
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  {[
                    "Enterprise Risk Management",
                    "Investment Management",
                    "Portfolio Management",
                    "Wealth Management",
                    "IFRS Standards",
                    "Internal Audit",
                    "Trade Finance",
                    "Code of Ethics"
                  ].map((area, idx) => (
                    <Grid item xs={6} key={idx}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          transition: "all 0.2s ease",
                          "&:hover": { bgcolor: `${pink}08`, transform: "translateX(5px)" }
                        }}
                      >
                        <VerifiedIcon sx={{ color: pink, fontSize: 20 }} />
                        <Typography variant="body2" fontWeight={600} color="#1e293b">
                          {area}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack spacing={3}>
                <Card
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    border: `2px solid ${pink}10`,
                    transition: "all 0.3s ease",
                    "&:hover": { boxShadow: "0 8px 32px rgba(0,0,0,0.12)", borderColor: pink }
                  }}
                >
                  <Typography variant="h6" fontWeight={700} color={pink} mb={2}>
                    Specialized In
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box>
                      <Typography variant="body2" fontWeight={600} color="#1e293b" mb={1}>
                        Customized Training Programs
                      </Typography>
                      <Typography variant="caption" color="#64748b">
                        Identifying core organizational needs and designing tailored training
                        programs for professional development and growth.
                      </Typography>
                    </Box>
                    <Divider />
                    <Box>
                      <Typography variant="body2" fontWeight={600} color="#1e293b" mb={1}>
                        Talent Development
                      </Typography>
                      <Typography variant="caption" color="#64748b">
                        Identifying and transforming talent pools, reengineering professionals
                        to become future leaders in their field.
                      </Typography>
                    </Box>
                    <Divider />
                    <Box>
                      <Typography variant="body2" fontWeight={600} color="#1e293b" mb={1}>
                        Youth Empowerment
                      </Typography>
                      <Typography variant="caption" color="#64748b">
                        Motivating young minds and making them responsible individuals who add
                        value to society.
                      </Typography>
                    </Box>
                  </Box>
                </Card>

                <Card
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    bgcolor: `${cyan}08`,
                    border: `2px solid ${cyan}20`,
                    transition: "all 0.3s ease",
                    "&:hover": { boxShadow: "0 8px 32px rgba(0,0,0,0.12)", borderColor: cyan }
                  }}
                >
                  <Typography variant="h6" fontWeight={700} color={cyan} mb={2}>
                    Philosophy
                  </Typography>
                  <Typography variant="body2" color="#64748b" fontStyle="italic" lineHeight={1.7}>
                    "Transforming today's professionals and youth into future leaders in their
                    own profession through effective decision making, conceptualization, and
                    innovative solutions."
                  </Typography>
                </Card>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Our Team Section */}
      <Box id="team" sx={{ bgcolor: "#f8fafc", py: 10, position: "relative", zIndex: 1 }}>
        <Container maxWidth="lg">
          <Fade in={visible} timeout={1000}>
            <Box mb={6}>
              <Box display="flex" alignItems="center" justifyContent="center" gap={2} mb={2}>
                <GroupsIcon sx={{ fontSize: 40, color: purple }} />
                <Typography variant="h3" fontWeight={900} color="#1e293b" textAlign="center">
                  Our{" "}
                  <span
                    style={{
                      background: `linear-gradient(135deg, ${purple}, ${cyan})`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent"
                    }}
                  >
                    Team
                  </span>
                </Typography>
              </Box>
              <Typography variant="body1" color="#64748b" textAlign="center" maxWidth="700px" mx="auto">
                Meet the experienced professionals dedicated to your success.
              </Typography>
            </Box>
          </Fade>

          <Grid container spacing={4} justifyContent="center">
            {teamDetails.map((member, idx) => (
              <Grid item xs={12} md={6} key={idx}>
                <Grow
                  in={visible}
                  timeout={1000}
                  style={{ transformOrigin: "0 0 0" }}
                  {...(visible ? { timeout: 1000 + idx * 200 } : {})}
                >
                  <Card
                    sx={{
                      borderRadius: 4,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                      border: `2px solid ${member.color}20`,
                      position: "relative",
                      overflow: "hidden",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      "&:before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "6px",
                        background: member.color,
                        transform: "scaleX(0)",
                        transformOrigin: "left",
                        transition: "transform 0.3s ease"
                      },
                      "&:hover": {
                        boxShadow: "0 16px 48px rgba(0,0,0,0.16)",
                        transform: "translateY(-10px) scale(1.02)",
                        borderColor: member.color,
                        "&:before": { transform: "scaleX(1)" }
                      }
                    }}
                  >
                    <CardContent sx={{ p: 4, flexGrow: 1, display: "flex", flexDirection: "column" }}>
                      <Box display="flex" alignItems="center" gap={3} mb={3}>
                        <Avatar
                          src={member.image}
                          alt={member.name}
                          sx={{
                            width: 80,
                            height: 80,
                            bgcolor: `${member.color}20`,
                            color: member.color,
                            fontWeight: 700,
                            fontSize: 30,
                            border: `2px solid ${member.color}`
                          }}
                        >
                          {member.initials}
                        </Avatar>
                        <Box>
                          <Typography variant="h5" fontWeight={800} color="#1e293b" mb={0.5}>
                            {member.name}
                          </Typography>
                          <Typography variant="subtitle2" color={member.color} fontWeight={600}>
                            {member.role}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" color="#64748b" lineHeight={1.8} mb={4} flexGrow={1}>
                        {member.bio}
                      </Typography>
                      <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<PictureAsPdfIcon />}
                        onClick={() => window.open(member.file, "_blank")}
                        sx={{
                          borderColor: member.color,
                          color: member.color,
                          fontWeight: 700,
                          py: 1.2,
                          borderRadius: 2,
                          textTransform: "none",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": {
                            borderColor: member.color,
                            bgcolor: `${member.color}15`,
                            transform: "translateY(-3px) scale(1.05)"
                          }
                        }}
                      >
                        View Full Profile
                      </Button>
                    </CardContent>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact / Footer */}
      <Box
        id="contact"
        sx={{
          bgcolor: "white",
          pt: 7,
          pb: 3,
          borderTop: `3px solid ${cyan}`,
          position: "relative",
          zIndex: 1
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box>
                <img src={logo} alt="Footer Logo" style={{ height: 44 }} />
                <Typography variant="body2" color="#64748b" mt={2}>
                  Interactive & global finance education
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Typography variant="h6" color="#1e293b" fontWeight={800} mb={2}>
                Contact Us
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  sx={{ transition: "all 0.3s ease", cursor: "pointer", "&:hover": { transform: "translateX(5px)", color: cyan } }}
                >
                  <PhoneIcon sx={{ color: cyan }} />
                  <Typography variant="body2" color="#64748b">+968 9200 2435</Typography>
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  sx={{ transition: "all 0.3s ease", cursor: "pointer", "&:hover": { transform: "translateX(5px)", color: pink } }}
                >
                  <EmailIcon sx={{ color: pink }} />
                  <a
                    href="mailto:info@drlabeebacademy.com"
                    style={{ color: "#64748b", fontSize: "0.875rem", textDecoration: "underline", fontWeight: 500 }}
                  >
                    info@drlabeebacademy.com
                  </a>
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  sx={{ transition: "all 0.3s ease", cursor: "pointer", "&:hover": { transform: "translateX(5px)", color: orange } }}
                >
                  <LocationOnIcon sx={{ color: orange }} />
                  <Typography variant="body2" color="#64748b">Muscat, Oman</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="h6" color="#1e293b" fontWeight={800} mb={2}>
                Connect
              </Typography>
              <Box display="flex" gap={2}>
                <IconButton
                  onClick={() => window.open(SOCIAL_LINKS.instagram, "_blank")}
                  sx={{
                    bgcolor: `${cyan}10`,
                    color: cyan,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": { bgcolor: "#E1306C", color: "white", transform: "translateY(-5px) rotate(360deg)" }
                  }}
                >
                  <InstagramIcon />
                </IconButton>
                <IconButton
                  onClick={() => window.open(SOCIAL_LINKS.linkedin, "_blank")}
                  sx={{
                    bgcolor: `${pink}10`,
                    color: pink,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": { bgcolor: "#0077B5", color: "white", transform: "translateY(-5px) rotate(360deg)" }
                  }}
                >
                  <LinkedInIcon />
                </IconButton>
                <IconButton
                  onClick={() => window.open(SOCIAL_LINKS.youtube, "_blank")}
                  sx={{
                    bgcolor: `${orange}10`,
                    color: orange,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": { bgcolor: "#FF0000", color: "white", transform: "translateY(-5px) rotate(360deg)" }
                  }}
                >
                  <YouTubeIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </Container>
        <Divider sx={{ my: 3 }} />
        <Typography variant="body2" color="#94a3b8" textAlign="center">
          © {new Date().getFullYear()} Dr. Labeeb Academy. All rights reserved.
        </Typography>
      </Box>

      <style>{`
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-50px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Box>
  );
}

export default Home;